/* eslint-disable @typescript-eslint/no-unsafe-function-type */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useRouter } from 'vue-router'
import { procAtSignout, setLastAccessDate } from '../utils/miiaFwCognito'
import { useLoadingView } from './useLoadingView'
import { useMiiaFwStore } from '@/stores/miiaFwStore'
import { storeToRefs } from 'pinia'
import { watch } from 'vue'
import type { TokenInfo } from '../types/cognito/auth'
import MiiaFwConfig, { type EndpointSetting } from '@/configs/config'
import GatewayClient from '@/features/miiaFw/utils/MiiaFwApiGatewayClient'
import { config as awsConfig, Credentials, type AWSError } from 'aws-sdk'
import { useToken } from './useToken'
import { webApiCall } from '../utils/miiaFwUtils'
import { useCommonModalStore } from '@/stores/commonModalStore'

export const useApiGw = () => {
  const router = useRouter()

  const miiaFwStore = useMiiaFwStore()
  const { refreshState, isAuthorized } = storeToRefs(miiaFwStore)

  const { setLoadingView } = useLoadingView()
  const { refreshToken, getTokens } = useToken()
  const commonModalStore = useCommonModalStore()

  /**
   * API-GWが提供する関数を実行する(POSTメソッド限定)。
   * @param url WebAPIのURL
   * @param path WebAPIのサブディレクトリ
   * @param body リクエスト時に送信するメッセージボディ
   * @param preCb 関数実行前に呼び出されるコールバック関数
   * @param successCb 関数実行成功後に呼び出されるコールバック関数
   * @param exceptionCb 例外受信時に呼び出されるコールバック関数
   * @param unknownCb 予期せぬエラー（アクセス回線障害）発生時に呼び出されるコールバック関数
   * @param alwaysCb 処理終了後に必ず呼び出されるコールバック関数
   * @param loadingView ローディング画面を表示するかどうか
   */
  const invokeApiGwFunc = (
    url: string | EndpointSetting,
    path: string,
    body: object,
    preCb: () => void,
    successCb: (data: any) => void,
    exceptionCb: (message: any) => void,
    unknownCb: () => void,
    alwaysCb: () => void,
    loadingView: boolean = true
  ): void => {
    if (MiiaFwConfig.config.authorization.mustAuth) {
      procUnderAuthed((tokens: TokenInfo) => {
        invokeApiGwFuncWithToken(
          tokens,
          url,
          path,
          body,
          preCb,
          successCb,
          exceptionCb,
          unknownCb,
          alwaysCb
        )
      }, loadingView)
    } else {
      invokeApiGwFuncWithoutToken(
        url,
        path,
        body,
        preCb,
        successCb,
        exceptionCb,
        unknownCb,
        alwaysCb
      )
    }
  }

  /**
   * invokeApiGwFuncが呼び出す、認証されているときに実行を許可する処理の雛形。
   * トークンをセッションスレージから取得出来た場合は認証の必要なAPIを実行し、取得できなかった場合にはアラートメッセージを出力してトップページに遷移させる。
   * @param callback 認証が必要な動作を行うコールバック関数
   * @param loadingView ローディング画面を表示するかどうか
   */
  const procUnderAuthed = async (
    callback: (tokens: TokenInfo) => void,
    loadingView: boolean = true
  ): Promise<void> => {
    const invokeCallback = (): void => {
      const tokens = getTokens()
      if (tokens !== null) {
        callback(tokens)
      } else {
        if (loadingView) setLoadingView(false)
        void procAtSignout().then(async () => {
          commonModalStore.openAlertModal({ body: '許可されていない操作です。(tokens)' })
          if (router.currentRoute.value.path !== '/') void router.push({ path: '/' }) // 同じページに遷移させるとNavigation Errorになる。
        })
      }
    }
    // 処理中表示
    if (loadingView) setLoadingView(true)
    if (isAuthorized.value) {
      invokeCallback()
    } else {
      const unwatch = watch(isAuthorized, newVal => {
        if (newVal === true) {
          invokeCallback()
        } else {
          if (loadingView) setLoadingView(false)
          void procAtSignout().then(async () => {
            commonModalStore.openAlertModal({ body: '許可されていない操作です。(Signout)' })
            if (router.currentRoute.value.path !== '/') {
              void router.push({ path: '/' })
            } // 同じページに遷移させるとNavigation Errorになる。
          })
        }
        unwatch() // 監視を解除
        clearTimeout(timerObj)
      })
      // 5秒以内にトークンを取得できなかった場合はタイムアウト
      const timerObj = setTimeout(() => {
        unwatch()
        if (refreshState.value?.onRefresh) {
          // トークンリフレッシュ中は再帰的に自分を実行する。
          procUnderAuthed(callback, loadingView)
        } else {
          void procAtSignout().then(async () => {
            commonModalStore.openAlertModal({ body: '許可されていない操作です。(unwatch)' })
            if (loadingView) setLoadingView(false)
            if (router.currentRoute.value.path !== '/') {
              void router.push({ path: '/' })
            } // 同じページに遷移させるとNavigation Errorになる。
          })
        }
      }, 5000)
    }
  }

  const getEndpoint = (endpoint: string | EndpointSetting): string => {
    return typeof endpoint === 'string' ? endpoint : endpoint.endpoint
  }

  /**
   * API-GWが提供する関数を実行する(POST限定、トークンによる認可処理が必要)。
   * invokeApiGwFuncが呼び出す、認可が必要な関数の処理を行う関数である。
   * @param tokens getTokensで取得できるセッション情報
   * @param url WebAPIのURL
   * @param path WebAPIのサブディレクトリ
   * @param body リクエスト時に送信するメッセージボディ
   * @param preCb 関数実行前に呼び出されるコールバック関数
   * @param successCb 関数実行成功後に呼び出されるコールバック関数
   * @param exceptionCb 例外受信時に呼び出されるコールバック関数
   * @param unknownCb 予期せぬエラー（アクセス回線障害）発生時に呼び出されるコールバック関数
   * @param alwaysCb 処理終了後に必ず呼び出されるコールバック関数
   * @param isRetryable 一度だけリトライするかどうか
   */
  const invokeApiGwFuncWithToken = (
    tokens: TokenInfo,
    url: string | EndpointSetting,
    path: string,
    body: any,
    preCb: () => void,
    successCb: (data: any) => void,
    exceptionCb: (message: any) => void,
    unknownCb: () => void,
    alwaysCb: () => void,
    isRetryable: boolean = true
  ): void => {
    preCb()

    const apigClientFactory = GatewayClient
    const pathParams = {
      // GET利用時の変数(クエリストリング)
    }
    const method = 'POST'
    let additionalParams = {}
    if (tokens.provider.toLowerCase() === 'cognito') {
      if (
        Object.prototype.hasOwnProperty.call(MiiaFwConfig.config.authorization, 'method') &&
        MiiaFwConfig.config.authorization.method?.toLowerCase() === 'iam'
      ) {
        // AWS_IAM
        additionalParams = {
          // AWS_IAMで認可する場合は、ヘッダを設定すると署名の検証で失敗する。
          queryParams: {}
        }
        // AWS_IAMで認可する場合は、HTTPヘッダを設定すると署名の検証で失敗するのでBodyでAccess Tokenを渡す。
        if (Object.prototype.hasOwnProperty.call(body, 'params')) {
          body.access_token = tokens.access_token
          body.session_id = tokens.session_id
        }
        ;(awsConfig.credentials as Credentials).get((err: AWSError | undefined) => {
          if (!err) {
            const setting = {
              invokeUrl: getEndpoint(url),
              accessKey: (awsConfig.credentials as Credentials).accessKeyId,
              secretKey: (awsConfig.credentials as Credentials).secretAccessKey,
              sessionToken: (awsConfig.credentials as Credentials).sessionToken,
              region: MiiaFwConfig.config.region
            }
            const isUrlString = typeof url === 'string'
            const endpoint = isUrlString ? (url as string) : (url as EndpointSetting).endpoint
            // if (!isUrlString) setting.awsEndpoint = (url as EndpointSetting).awsEndpoint
            const settings = !isUrlString
              ? { ...setting, awsEndpoint: (url as EndpointSetting).awsEndpoint }
              : setting
            const apigClient = apigClientFactory.newClient(settings)
            invokeApiGwFuncWithAuthProc(
              apigClient,
              pathParams,
              method,
              additionalParams,
              endpoint,
              path,
              body,
              preCb,
              successCb,
              exceptionCb,
              unknownCb,
              alwaysCb,
              isRetryable
            )
          } else {
            void refreshToken()
              .then((argTokens: any) => {
                ;(awsConfig.credentials as Credentials).get((err: AWSError | undefined) => {
                  if (!err) {
                    const isUrlString = typeof url === 'string'
                    const endpoint = isUrlString
                      ? (url as string)
                      : (url as EndpointSetting).endpoint
                    // if (!isUrlString) setting.awsEndpoint = (url as EndpointSetting).awsEndpoint
                    const setting = {
                      invokeUrl: endpoint,
                      accessKey: (awsConfig.credentials as Credentials).accessKeyId,
                      secretKey: (awsConfig.credentials as Credentials).secretAccessKey,
                      sessionToken: (awsConfig.credentials as Credentials).sessionToken,
                      region: MiiaFwConfig.config.region
                    }
                    // if (!isUrlString) setting.awsEndpoint = (url as EndpointSetting).awsEndpoint
                    const settings = !isUrlString
                      ? { ...setting, awsEndpoint: (url as EndpointSetting).awsEndpoint }
                      : setting
                    body.access_token = argTokens.access_token
                    body.session_id = argTokens.session_id
                    const apigClient = apigClientFactory.newClient(settings)
                    invokeApiGwFuncWithAuthProc(
                      apigClient,
                      pathParams,
                      method,
                      additionalParams,
                      url,
                      path,
                      body,
                      preCb,
                      successCb,
                      exceptionCb,
                      unknownCb,
                      alwaysCb,
                      isRetryable
                    )
                  } else {
                    void procAtSignout().then(async () => {
                      commonModalStore.openAlertModal({ body: err.message })
                      if (router.currentRoute.value.path !== '/') {
                        void router.push({ path: '/' })
                      } // 同じページに遷移させるとNavigation Errorになる。
                    })
                  }
                })
              })
              .catch(refreshError => {
                // procAtSignout()は失敗しない。
                // 【要検討】Refresh tokenを使用してAccess tokenをリフレッシュできなかった場合は、Top画面に戻るか再認証してリトライか?
                void procAtSignout().then(async () => {
                  commonModalStore.openAlertModal({ body: refreshError.message })
                  if (router.currentRoute.value.path !== '/') {
                    void router.push({ path: '/' })
                  } // 同じページに遷移させるとNavigation Errorになる。
                })
              })
          }
        })
      } else if (
        Object.prototype.hasOwnProperty.call(MiiaFwConfig.config.authorization, 'method') &&
        MiiaFwConfig.config.authorization.method?.toLowerCase() === 'custom_authorizer'
      ) {
        additionalParams = {
          // Custom Authorizerで認可する場合は、Authorizationヘッダにid tokenを含める必要あり
          headers: {
            Authorization: tokens.id_token,
            'X-miiafw-token': tokens.access_token
          },
          queryParams: {}
        }
        const setting = { invokeUrl: getEndpoint(url) }
        const apigClient = apigClientFactory.newClient(setting)
        invokeApiGwFuncWithAuthProc(
          apigClient,
          pathParams,
          method,
          additionalParams,
          url,
          path,
          body,
          preCb,
          successCb,
          exceptionCb,
          unknownCb,
          alwaysCb,
          isRetryable
        )
      } else {
        void procAtSignout().then(async () => {
          commonModalStore.openAlertModal({ body: '対応していない認可方式です。' })
          if (router.currentRoute.value.path !== '/') void router.push({ path: '/' }) // 同じページに遷移させるとNavigation Errorになる。
        })
      }
      // } else if (tokens.provider.toLowerCase() === 'google') {
      //   additionalParams = {
      //     headers: { Authorization: tokens.id_token },
      //     queryParams: {}
      //   }
      //   const setting = { invokeUrl: url }
      //   const apigClient = apigClientFactory.newClient(setting)
      //   invokeApiGwFuncWithAuthProc(
      //     apigClient,
      //     pathParams,
      //     method,
      //     additionalParams,
      //     url,
      //     path,
      //     body,
      //     preCb,
      //     successCb,
      //     exceptionCb,
      //     unknownCb,
      //     alwaysCb,
      //     isRetryable
      //   )
      // } else if (tokens.provider.toLowerCase() === 'facebook') {
      //   additionalParams = {
      //     // facebook: accessトークン
      //     headers: { Authorization: tokens.access_token },
      //     queryParams: {}
      //   }
      //   const setting = { invokeUrl: url }
      //   const apigClient = apigClientFactory.newClient(setting)
      //   invokeApiGwFuncWithAuthProc(
      //     apigClient,
      //     pathParams,
      //     method,
      //     additionalParams,
      //     url,
      //     path,
      //     body,
      //     preCb,
      //     successCb,
      //     exceptionCb,
      //     unknownCb,
      //     alwaysCb,
      //     isRetryable
      //   )
      // } else if (tokens.provider.toLowerCase() === 'twitter') {
      //   additionalParams = {
      //     // twitter: Authトークンとトークンシークレットを「;」で連結したもの

      //     headers: { Authorization: `${tokens.auth_token};${tokens.token_secret}` },
      //     queryParams: {}
      //   }
      //   const setting = { invokeUrl: url }
      //   const apigClient = apigClientFactory.newClient(setting)
      //   invokeApiGwFuncWithAuthProc(
      //     apigClient,
      //     pathParams,
      //     method,
      //     additionalParams,
      //     url,
      //     path,
      //     body,
      //     preCb,
      //     successCb,
      //     exceptionCb,
      //     unknownCb,
      //     alwaysCb,
      //     isRetryable
      //   )
    } else {
      void procAtSignout().then(async () => {
        commonModalStore.openAlertModal({ body: '対応していないIDプロバイダが設定されました。' })
        if (router.currentRoute.value.path !== '/') void router.push({ path: '/' }) // 同じページに遷移させるとNavigation Errorになる。
      })
    }
  }

  /**
   * API-GWが提供する関数を実行する(POST限定、トークンによる認可処理が不要)。
   * invokeApiGwFuncが呼び出す、認可が不要な関数の処理を行う関数である。
   * @param url WebAPIのURL
   * @param path WebAPIのサブディレクトリ
   * @param body リクエスト時に送信するメッセージボディ
   * @param preCb 関数実行前に呼び出されるコールバック関数
   * @param successCb 関数実行成功後に呼び出されるコールバック関数
   * @param exceptionCb 例外受信時に呼び出されるコールバック関数
   * @param unknownCb 予期せぬエラー（アクセス回線障害）発生時に呼び出されるコールバック関数
   * @param alwaysCb 処理終了後に必ず呼び出されるコールバック関数
   */
  const invokeApiGwFuncWithoutToken = (
    url: string | EndpointSetting,
    path: string,
    body: object,
    preCb: Function,
    successCb: Function,
    exceptionCb: Function,
    unknownCb: Function,
    alwaysCb: Function
  ): void => {
    const invokeApi = async (uri: string, params: any): Promise<any> => {
      return new Promise((resolve, reject) => {
        webApiCall(uri, 'POST', {}, params, 'json')
          .then(data => {
            if (typeof data === 'string') {
              // IE対応
              data = JSON.parse(data)
            }
            resolve(data)
          })
          .catch(error => {
            if (typeof error === 'string') {
              // IE対応
              error = JSON.parse(error)
            }
            reject({ message: error.message })
          })
      })
    }
    preCb()
    void invokeApi(url + path, body)
      .then((data: any) => {
        if (Object.prototype.hasOwnProperty.call(data, 'error')) {
          // Lambdaが例外をスローした場合
          exceptionCb(data.error)
        } else {
          successCb(data)
        }
      })
      .catch(error => {
        console.error(error)
        unknownCb()
      })
      .then(() => {
        // then -> catch -> then = done -> fail -> always
        setLastAccessDate(Date.now())
        alwaysCb()
      })
  }

  // API Gatewayの認可対応のAPI実行処理本体
  /**
   * invokeApiGwFuncWithTokenが呼び出す、API-GWが提供する認可処理を行った後API実行を行う処理本体。
   * @param apigClient aws-api-gateway-clientパッケージによって生成されたクライアント
   * @param pathParams GETメソッド使用時のパスリクエストパラメータ
   * @param method HTTPメソッド
   * @param additionalParams クエリが必要なときに入力するパラメータ
   * @param url WebAPIのURL
   * @param path WebAPIのサブディレクトリ
   * @param body リクエスト時に送信するメッセージボディ
   * @param preCb 関数実行前に呼び出されるコールバック関数
   * @param successCb 関数実行成功後に呼び出されるコールバック関数
   * @param exceptionCb 例外受信時に呼び出されるコールバック関数
   * @param unknownCb 予期せぬエラー（アクセス回線障害）発生時に呼び出されるコールバック関数
   * @param alwaysCb 処理終了後に必ず呼び出されるコールバック関数
   * @param isRetryable 一度だけリトライするかどうか
   */
  const invokeApiGwFuncWithAuthProc = (
    apigClient: any,
    pathParams: object,
    method: string,
    additionalParams: object,
    url: string | EndpointSetting,
    path: string,
    body: object,
    preCb: () => void,
    successCb: (data: any) => void,
    exceptionCb: (message: any) => void,
    unknownCb: () => void,
    alwaysCb: () => void,
    isRetryable: boolean
  ): void => {
    apigClient
      .invokeApi(pathParams, path, method, additionalParams, body)
      .then((result: any) => {
        if (Object.prototype.hasOwnProperty.call(result.data, 'error')) {
          // Lambdaが例外をスローした場合
          exceptionCb(result.data.error)
        } else {
          successCb(result.data)
        }
      })
      .catch((error: any) => {
        // API GWが自動生成するAPIはCredential Errorを検出できないので1回だけリトライする。
        if (isRetryable) {
          refreshToken()
            .then(argTokens => {
              invokeApiGwFuncWithToken(
                argTokens as TokenInfo,
                url,
                path,
                body,
                preCb,
                successCb,
                exceptionCb,
                unknownCb,
                alwaysCb,
                false
              )
            })
            .catch(refreshError => {
              // procAtSignout()は失敗しない。
              // 【要検討】Refresh tokenを使用してAccess tokenをリフレッシュできなかった場合は、Top画面に戻るか再認証してリトライか?
              void procAtSignout().then(() => {
                if (router.currentRoute.value.path !== '/') {
                  void router.push({ path: '/' })
                } // 同じページに遷移させるとNavigation Errorになる。
                setLoadingView(false)
                exceptionCb(refreshError.message)
              })
            })
        } else {
          console.error(error)
          unknownCb()
        }
      })
      .then(() => {
        // then -> catch -> then = done -> fail -> always
        setLastAccessDate(Date.now())
        alwaysCb()
      })
  }

  return {
    invokeApiGwFunc
  }
}
